import { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send,
  Loader2,
  LogOut,
  Store
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ContactFormData } from "@/types/api";
import { AuthContext } from "@/context/AuthContext";

const Contact = () => {
  const navigate = useNavigate();
    const { isAuthenticated, currentUser, logout } = useContext(AuthContext);
  const { toast } = useToast();
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSelectChange = (value: string) => {
    setFormData({
      ...formData,
      subject: value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    
    // In a real app, we would use axios to submit the form
    // try {
    //   await axios.post('/api/contact', formData);
    //   setSubmitted(true);
    // } catch (err) {
    //   console.error("Contact form submission error:", err);
    //   setError("Failed to send message. Please try again later.");
    // } finally {
    //   setIsLoading(false);
    // }
    
    // Simulate API call
    setTimeout(() => {
      setSubmitted(true);
      setIsLoading(false);
    }, 1000);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <main className="min-h-screen w-full flex flex-col">
      {/* Navbar */}
      <div className="flex justify-between items-center px-8 py-4 bg-primary text-primary-foreground">
        <Link to="/" className="flex items-center gap-2 font-bold text-xl">
          <Store size={24} />
          Jaya Maruthi Store
        </Link>
        
        <div className="flex gap-6">
          {isAuthenticated ? (
            <>
              <Button variant="ghost" asChild>
                <Link to="/dashboard">Dashboard</Link>
              </Button>
              <Button variant="ghost" onClick={handleLogout}>
                <LogOut size={16} className="mr-2" />
                Logout
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" asChild>
                <Link to="/login">Login</Link>
              </Button>
              <Button variant="ghost" asChild>
                <Link to="/register">Register</Link>
              </Button>
              <Button variant="secondary" asChild>
                <Link to="/contact">Contact</Link>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 container py-16">
        <h1 className="text-4xl font-bold text-center mb-12">Get in Touch</h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Contact Information */}
          <Card className="md:col-span-1 bg-primary text-primary-foreground">
            <CardContent className="p-6">
              <h2 className="text-xl font-bold mb-6">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-primary-foreground/20 flex items-center justify-center">
                    <Phone size={20} />
                  </div>
                  <div>
                    <p className="text-sm opacity-80">Phone</p>
                    <p className="font-medium">+1 (555) 123-4567</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-primary-foreground/20 flex items-center justify-center">
                    <Mail size={20} />
                  </div>
                  <div>
                    <p className="text-sm opacity-80">Email</p>
                    <p className="font-medium">info@jmstore.com</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-primary-foreground/20 flex items-center justify-center">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <p className="text-sm opacity-80">Address</p>
                    <p className="font-medium">6F JC Street, Dharnampet</p>
                    <p className="font-medium">Gudiyatham, 623602</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-12">
                <p className="text-sm opacity-80">Business Hours</p>
                <p className="font-medium mt-2">Monday - Friday: 9:00 AM - 5:00 PM</p>
                <p className="font-medium">Saturday - Sunday: Closed</p>
              </div>
            </CardContent>
          </Card>
          
          {/* Contact Form */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Send us a Message</CardTitle>
              <CardDescription>
                Fill out the form below and we'll get back to you as soon as possible
              </CardDescription>
            </CardHeader>
            <CardContent>
              {submitted ? (
                <Alert className="bg-green-50 border-green-200 text-green-800 p-6">
                  <div className="flex flex-col gap-2">
                    <h3 className="text-lg font-semibold">Thank you for your message!</h3>
                    <AlertDescription>
                      We have received your inquiry and will get back to you as soon as possible.
                    </AlertDescription>
                    <Button 
                      variant="outline" 
                      className="mt-2 w-fit"
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({
                          name: "",
                          email: "",
                          subject: "",
                          message: ""
                        });
                      }}
                    >
                      Send Another Message
                    </Button>
                  </div>
                </Alert>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Your Name</Label>
                      <Input
                        id="name"
                        name="name"
                        placeholder="John Doe"
                        value={isAuthenticated && currentUser.name ? currentUser.name : formData.name}
                        onChange={handleInputChange}
                        required
                        disabled={isAuthenticated && currentUser.name ? true : false}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="name@example.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject</Label>
                    <Select 
                      value={formData.subject} 
                      onValueChange={handleSelectChange}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a subject" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="General Inquiry">General Inquiry</SelectItem>
                        <SelectItem value="Technical Support">Technical Support</SelectItem>
                        <SelectItem value="Pricing Question">Pricing Question</SelectItem>
                        <SelectItem value="Feature Request">Feature Request</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message">Your Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="How can we help you?"
                      rows={6}
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-fit" 
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send size={16} className="ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="py-6 text-center border-t mt-auto">
        <p className="text-sm text-muted-foreground">
          © 2025 Sukri Software
        </p>
      </div>
    </main>
  );
};

export default Contact;